import moment, { Moment } from "moment";
import React, { ChangeEvent, useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import Alert from "veneer/Alert";
import AutocompleteSelectInput from "veneer/AutocompleteSelectInput";
import Button from "veneer/Button";
import DateTimePicker from "veneer/DateTimePicker";
import Dialog from "veneer/Dialog";
import FormControlLabel from "veneer/FormControlLabel";
import List from "veneer/List";
import ListItem from "veneer/ListItem";
import Stack from "veneer/Stack";
import Switch from "veneer/Switch";
import Typography from "veneer/Typography";

import { ROOT_URL } from "cortex/ui/src/fleet/constant/mediaLibrary";
import { SCHEDULE_DEPLOYMENT } from "cortex/ui/src/fleet/redux-store/scheduled-deployments/actions";
import { useAppDispatch, useAppSelector } from "cortex/ui/src/hooks";
import { createPromiseApiAction } from "cortex/ui/src/redux-store/api-sagas";
import { getTargets } from "cortex/ui/src/redux-store/devices/selectors";
import {
  filterByProperty,
  makeGetAllAsArray,
} from "cortex/ui/src/redux-store/selectors";
import BackButton from "cortex/ui/src/ui/button";
import Notification from "cortex/ui/src/ui/notification";
import { Option } from "cortex/ui/src/ui/table/types";
import type { ResponseLike } from "cortex/ui/src/util/response";
import { getErrorMessage } from "cortex/ui/src/util/response";
import { Container, Panel } from "cortex/ui/src/view/layout";

interface DeployArgs {
  deployDate: string | null;
  deployNow: boolean;
  endDate: string | null;
  strategyId: number | string;
  targetIds: Array<number | string>;
}

interface DeployV2PageProps {
  scheduleDeployment: (serviceArgs: DeployArgs) => Promise<unknown>;
  strategies?: ReadonlyArray<{ id: number | string; name: string }>;
  targets?: ReadonlyArray<Option>;
}

interface DeployV2PageErrors {
  endDate?: string;
  selectedStrategy?: string;
  selectedTargetDevices?: string;
  startDate?: string;
}

const getStrategiesAsArray = makeGetAllAsArray<any>();
const getDevicesAsArray = makeGetAllAsArray<any>();

function getDefaultStartDate() {
  return moment().subtract(12, "hours");
}

function formatDate(date: Moment | null) {
  return date ? date.format("YYYY-MM-DD HH:mm:00") : null;
}

function getDateErrorMessage(
  deployNow: boolean,
  deployIndefinitely: boolean,
  startDate: Moment | null,
  endDate: Moment | null,
) {
  if (!startDate && !deployNow) {
    return "Please set a start date or check deploy now.";
  }

  if (!endDate && !deployIndefinitely) {
    return "Please set an end date or check keep running indefinitely.";
  }

  return null;
}

function renderDeploymentNotes() {
  return (
    <Alert severity="info">
      <Typography component="div" fontWeight="bold" mb={1}>
        Please Note:
      </Typography>
      <List disablePadding gap={1} listStyleType="disc">
        <ListItem disablePadding>
          Deployment dates are converted to the local time for the device(s)
        </ListItem>
        <ListItem disablePadding>
          Scheduling a deployment <em>without</em> an end date does not
          guarantee the deployment will run immediately.
          <br />
          <em>
            <strong>
              Deployments with end dates have higher priority than those
              without.
            </strong>
          </em>
        </ListItem>
      </List>
    </Alert>
  );
}

function DeployV2Page({
  scheduleDeployment,
  strategies = [],
  targets = [],
}: DeployV2PageProps) {
  const [deployIndefinitely, setDeployIndefinitely] = useState(true);
  const [deployNow, setDeployNow] = useState(true);
  const [endDate, setEndDate] = useState<Moment | null>(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [fieldErrors, setFieldErrors] = useState<DeployV2PageErrors>({});
  const [selectedStrategy, setSelectedStrategy] = useState<Option | null>(null);
  const [selectedTargetDevices, setSelectedTargetDevices] = useState<
    ReadonlyArray<Option>
  >([]);
  const [showModal, setShowModal] = useState(false);
  const [startDate, setStartDate] = useState<Moment | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [successLink, setSuccessLink] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const minimumDeploymentDate = moment().subtract(1, "days").startOf("day");

  const strategyOptions = useMemo(
    () =>
      strategies.map((strategy) => ({
        label: strategy.name,
        value: strategy.id,
      })),
    [strategies],
  );

  const validateForm = useCallback((): DeployV2PageErrors => {
    const errors: DeployV2PageErrors = {};
    const dateError = getDateErrorMessage(
      deployNow,
      deployIndefinitely,
      startDate,
      endDate,
    );

    if (dateError) {
      if (!startDate && !deployNow) {
        errors.startDate = dateError;
      }

      if (!endDate && !deployIndefinitely) {
        errors.endDate = dateError;
      }
    }

    if (selectedTargetDevices.length === 0) {
      errors.selectedTargetDevices =
        "Please select targets for your deployment.";
    }

    if (!selectedStrategy?.value) {
      errors.selectedStrategy = "Please select a strategy for your deployment.";
    }

    return errors;
  }, [
    deployIndefinitely,
    deployNow,
    endDate,
    selectedStrategy,
    selectedTargetDevices.length,
    startDate,
  ]);

  const clearMessages = useCallback(() => {
    setErrorMessage("");
    setSuccessLink("");
    setSuccessMessage("");
  }, []);

  const doDeploy = useCallback(
    async (args: DeployArgs) => {
      try {
        await scheduleDeployment(args);
        setErrorMessage("");
        setSuccessLink(`${ROOT_URL}/scheduled`);
        setSuccessMessage(
          "Strategy successfully scheduled to deploy on the target devices.",
        );
      } catch (error) {
        setErrorMessage(getErrorMessage(error as Error | ResponseLike | null));
        setSuccessLink("");
        setSuccessMessage("");
      } finally {
        setShowModal(false);
        setSubmitted(false);
      }
    },
    [scheduleDeployment],
  );

  const handleSelectStrategy = useCallback(
    (_event: unknown, value: Option | null) => {
      setSelectedStrategy(value);
      setFieldErrors((currentErrors) => ({
        ...currentErrors,
        selectedStrategy: undefined,
      }));
      clearMessages();
    },
    [clearMessages],
  );

  const handleSelectTargetDevices = useCallback(
    (_event: unknown, value: ReadonlyArray<Option> | null) => {
      setSelectedTargetDevices(value ?? []);
      setFieldErrors((currentErrors) => ({
        ...currentErrors,
        selectedTargetDevices: undefined,
      }));
      clearMessages();
    },
    [clearMessages],
  );

  const handleToggleDeployNow = useCallback(
    (checked: boolean) => {
      setDeployNow(checked);
      setStartDate((currentStartDate) => (checked ? null : currentStartDate));
      setFieldErrors((currentErrors) => ({
        ...currentErrors,
        startDate: undefined,
      }));
      clearMessages();
    },
    [clearMessages],
  );

  const handleToggleDeployIndefinitely = useCallback(
    (checked: boolean) => {
      setDeployIndefinitely(checked);
      setEndDate((currentEndDate) => (checked ? null : currentEndDate));
      setFieldErrors((currentErrors) => ({
        ...currentErrors,
        endDate: undefined,
      }));
      clearMessages();
    },
    [clearMessages],
  );

  const handleStartDateChange = useCallback(
    (value: Moment | null) => {
      setStartDate(value);
      setFieldErrors((currentErrors) => ({
        ...currentErrors,
        startDate: undefined,
      }));
      clearMessages();
    },
    [clearMessages],
  );

  const handleEndDateChange = useCallback(
    (value: Moment | null) => {
      setEndDate(value);
      setFieldErrors((currentErrors) => ({
        ...currentErrors,
        endDate: undefined,
      }));
      clearMessages();
    },
    [clearMessages],
  );

  const deploy = useCallback(() => {
    setFieldErrors({});
    const errors = validateForm();
    const hasErrors = Object.values(errors).some(Boolean);

    if (hasErrors) {
      setFieldErrors(errors);
      setShowModal(false);
      setSuccessLink("");
      setSuccessMessage("");
      return;
    }

    setSubmitted(true);
    void doDeploy({
      deployDate: formatDate(startDate || getDefaultStartDate()),
      deployNow,
      endDate: formatDate(deployIndefinitely ? null : endDate),
      strategyId: selectedStrategy!.value,
      targetIds: selectedTargetDevices.map((target) => target.value),
    });
  }, [
    deployIndefinitely,
    deployNow,
    doDeploy,
    endDate,
    selectedStrategy,
    selectedTargetDevices,
    startDate,
    validateForm,
  ]);

  const submit = useCallback(() => {
    if (deployNow) {
      setShowModal(true);
      return;
    }

    deploy();
  }, [deploy, deployNow]);

  return (
    <Container title="Deploy Strategy">
      <Panel width="60%">
        <Dialog
          commitDisabled={submitted}
          commitLabel="Confirm"
          commitLoading={submitted}
          onCancel={() => setShowModal(false)}
          onCommit={deploy}
          onDismiss={() => setShowModal(false)}
          open={showModal}
          title="Please Note"
          variant="modal"
        >
          {renderDeploymentNotes()}
        </Dialog>
        <Stack gap={2}>
          <Notification
            error={errorMessage}
            success={successMessage}
            successChildren={
              <ul>
                <li>
                  <Link to={successLink}>View Deployments</Link>
                </li>
              </ul>
            }
          />
          {renderDeploymentNotes()}
          <AutocompleteSelectInput<Option, false, false, false>
            dataTestId="deploy-strategy-select"
            getOptionKey={(option) => String(option.value)}
            inputProps={{
              error: Boolean(fieldErrors.selectedStrategy),
              helperText:
                fieldErrors.selectedStrategy ??
                "Required. Please select the target strategy you want to run on your devices.",
              label: "Strategy",
              required: true,
            }}
            isOptionEqualToValue={(option, value) =>
              option.value === value.value
            }
            onChange={handleSelectStrategy}
            options={strategyOptions}
            size="small"
            value={selectedStrategy}
          />
          <AutocompleteSelectInput<Option, true, false, false>
            dataTestId="deploy-target-select"
            disableCloseOnSelect
            getOptionKey={(option) => String(option.value)}
            inputProps={{
              error: Boolean(fieldErrors.selectedTargetDevices),
              helperText:
                fieldErrors.selectedTargetDevices ??
                "Required. Please select the target devices you want the above strategy to run on. You can select individual devices as well as tags.",
              label: "Target Devices",
              required: true,
            }}
            isOptionEqualToValue={(option, value) =>
              option.value === value.value
            }
            multiple
            onChange={handleSelectTargetDevices}
            options={[...targets]}
            size="small"
            value={[...selectedTargetDevices]}
          />
          <FormControlLabel
            control={
              <Switch
                checked={deployNow}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  handleToggleDeployNow(event.target.checked)
                }
              />
            }
            label="Deploy Now"
          />
          {!deployNow && (
            <DateTimePicker
              error={Boolean(fieldErrors.startDate)}
              format="MMM Do, YYYY hh:mm A"
              fullWidth
              helperText={
                fieldErrors.startDate ??
                "Please select the date and time you would you like your strategy to start running."
              }
              label="Start Date and Time"
              minDate={minimumDeploymentDate}
              onChange={handleStartDateChange}
              required={!deployNow}
              size="small"
              value={deployNow ? null : startDate}
            />
          )}
          <FormControlLabel
            control={
              <Switch
                checked={deployIndefinitely}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  handleToggleDeployIndefinitely(event.target.checked)
                }
              />
            }
            label="Keep Running Indefinitely"
          />
          {!deployIndefinitely && (
            <DateTimePicker
              error={Boolean(fieldErrors.endDate)}
              format="MMM Do, YYYY hh:mm A"
              fullWidth
              helperText={
                fieldErrors.endDate ??
                "Please select the date and time you would you like your strategy to finish running."
              }
              label="End Date and Time"
              minDate={minimumDeploymentDate}
              onChange={handleEndDateChange}
              required
              size="small"
              value={endDate}
            />
          )}
          <Stack alignItems="center" direction="row" gap={1}>
            <BackButton />
            <Button
              dataTestId="deploy-submit"
              disabled={submitted}
              loading={submitted}
              onClick={submit}
            >
              Deploy
            </Button>
          </Stack>
        </Stack>
      </Panel>
    </Container>
  );
}

export default function DeployPage() {
  const dispatch = useAppDispatch();
  const strategies = useAppSelector((state: any) => {
    const unarchivedStrategies = filterByProperty(
      state.app.strategies,
      "archived",
      false,
    );
    return getStrategiesAsArray(unarchivedStrategies);
  });
  const targets = useAppSelector((state: any) =>
    getTargets(getDevicesAsArray(state.global.devices), true),
  );

  return (
    <DeployV2Page
      scheduleDeployment={(serviceArgs) => {
        const { deployDate, deployNow, endDate, strategyId, targetIds } =
          serviceArgs;

        return createPromiseApiAction(dispatch, SCHEDULE_DEPLOYMENT, [
          strategyId,
          targetIds,
          deployDate,
          endDate,
          deployNow,
        ]);
      }}
      strategies={strategies}
      targets={targets}
    />
  );
}
